import { inject, injectable } from 'inversify';
import {
  CreateOrderParams,
  CreateOrderResponse,
  GetAllOrdersRequestParams,
  GetAllOrdersResponse,
  GetOrderByFiltersRequestParams,
  GetOrderByIdRequestParams,
  MarkOrderAsDeliveredParams,
  MarkOrderAsDeliveredResponse,
  OrdersServiceInterface,
  UploadPaymentReceiptParams,
} from './OrdersService.types';
import HttpClient from '../../../http';
import { AppSubdomains, LocalStorageKeys, OrderInfo } from 'src/types/common';
import { backendUrl } from 'src/config';
import { formatDate } from 'date-fns';
import { es } from 'date-fns/locale';
import { getSubdomain } from 'src/utils/domain/getSubdomain';
import { getLocalStorageItem } from 'src/utils/localStorage';

@injectable()
class OrdersService implements OrdersServiceInterface {
  @inject(HttpClient) private client!: HttpClient;

  async getOrderById(params: GetOrderByIdRequestParams) {
    const { accessToken, orderId } = params;

    return await this.client.getClient().get<OrderInfo>(`/orders/${orderId}`, {
      baseURL: backendUrl,
      headers: {
        Authorization: `Token ${accessToken}`,
      },
    });
  }

  async getOrderByFilters(params: GetOrderByFiltersRequestParams) {
    const { accessToken, cartId: cart_id, paymentId: payment_id } = params;
    if (!cart_id && !payment_id) {
      throw new Error('You must provide a cart_id or a payment_id');
    }

    const reqParams = {
      ...(cart_id && { cart_id }),
      ...(payment_id && { payment_id }),
    };

    return await this.client
      .getClient()
      .get<OrderInfo>(`/orders/find_by_filters/`, {
        baseURL: backendUrl,
        headers: {
          Authorization: `Token ${accessToken}`,
        },
        params: reqParams,
      });
  }

  async getOrders({ accessToken }: GetAllOrdersRequestParams) {
    return await this.client
      .getClient()
      .get<GetAllOrdersResponse>(`/orders/all`, {
        baseURL: backendUrl,
        headers: {
          Authorization: `Token ${accessToken}`,
        },
      });
  }

  async markAsDelivered(params: MarkOrderAsDeliveredParams) {
    const { accessToken, orderId } = params;

    return await this.client.getClient().post<MarkOrderAsDeliveredResponse>(
      `/orders/${orderId}/confirm-delivery/`,
      {},
      {
        baseURL: backendUrl,
        headers: {
          Authorization: `Token ${accessToken}`,
        },
      }
    );
  }

  async createOrder({
    cart,
    accessToken,
    extra_data,
    pari_wallet,
    ...params
  }: CreateOrderParams) {
    const deliveryMethodInfo = extra_data?.delivery_info;
    const deliveryMethodData =
      deliveryMethodInfo?.method === 'scheduled'
        ? {
            ...deliveryMethodInfo,
            ...(deliveryMethodInfo.date && {
              date: formatDate(
                new Date(deliveryMethodInfo.date.toISOString() as string),
                'dd-MMM-yyyy',
                { locale: es }
              ),
            }),
          }
        : deliveryMethodInfo;

    const subdomain = getSubdomain(AppSubdomains, AppSubdomains.Pari);
    const origin = subdomain === AppSubdomains.Terea ? 'pari-oms' : null;
    const tereaJwt =
      subdomain === AppSubdomains.Terea
        ? getLocalStorageItem(LocalStorageKeys.VerificationToken)
        : null;

    return await this.client.getClient().post<CreateOrderResponse>(
      `/carts/${cart}/order-payment/`,
      {
        ...params,
        ...(pari_wallet && { pari_wallet }),
        ...(origin && { origin }),
        ...(tereaJwt && { token: tereaJwt }),
        extra_data: {
          origin: 'mobile-app',
          ...extra_data,
          delivery_info: deliveryMethodData,
        },
      },
      {
        baseURL: backendUrl,
        headers: {
          Authorization: `Token ${accessToken}`,
        },
      }
    );
  }

  async uploadPaymentReceipt({
    accessToken,
    orderId,
    paymentReceipt,
  }: UploadPaymentReceiptParams) {
    const formData = new FormData();
    formData.append('payment_receipt', paymentReceipt);

    const response = await this.client
      .getClient()
      .put(`/order-payments/${orderId}/`, formData, {
        baseURL: backendUrl,
        headers: {
          Authorization: `Token ${accessToken}`,
          'Content-Type': 'multipart/form-data',
        },
      });

    return response.data;
  }
}

export default OrdersService;
